import type { ApiResponse } from '@repo/shared-types';
import { useAdminAuthStore } from '../stores/adminAuthStore';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

export interface ApiError extends Error {
  code?: string;
  status?: number;
}

function apiErrorFrom(message: string, code: string | undefined, status: number): ApiError {
  return Object.assign(new Error(message), { code, status });
}

// Dedupe concurrent 401s behind a single in-flight refresh call.
let refreshPromise: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  const { refreshToken, updateTokens, clearAuth } = useAdminAuthStore.getState();

  if (!refreshToken) {
    clearAuth();
    window.location.href = '/login';
    throw new Error('No refresh token');
  }

  const res = await fetch(`${API_BASE}/admin/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });

  const json = await res.json().catch(() => null);

  if (!res.ok || !json?.success) {
    clearAuth();
    window.location.href = '/login';
    throw new Error('Token refresh failed');
  }

  const tokens = json.data as { accessToken: string; refreshToken: string };
  updateTokens(tokens);
  return tokens.accessToken;
}

function getRefreshedToken(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/** Authenticated fetch against admin endpoints. Attaches the admin access
 * token, retries once via a deduped refresh on 401, and unwraps the
 * `{ success, data }` envelope. Callers toast errors themselves. */
export async function adminFetchApi<T>(path: string, init?: RequestInit): Promise<T> {
  const { accessToken } = useAdminAuthStore.getState();

  const isFormData = init?.body instanceof FormData;
  const headers: Record<string, string> = {
    // A FormData body must set its own multipart Content-Type, boundary
    // included — forcing JSON here silently corrupts the upload.
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(init?.headers as Record<string, string>),
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const url = `${API_BASE}${path}`;
  let res = await fetch(url, { ...init, headers });

  if (res.status === 401 && accessToken) {
    const newToken = await getRefreshedToken();
    headers['Authorization'] = `Bearer ${newToken}`;
    res = await fetch(url, { ...init, headers });
  }

  const text = await res.text();
  let json: ApiResponse<T>;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`Failed to parse JSON response: ${text}`);
  }

  if (!json.success) {
    throw apiErrorFrom(json.error.message, json.error.code, res.status);
  }

  return json.data;
}

/** Fetch without auth — for public admin endpoints (login). */
export async function adminFetchPublicApi<T>(path: string, init?: RequestInit): Promise<T> {
  const url = `${API_BASE}${path}`;
  const res = await fetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
  });

  const text = await res.text();
  let json: ApiResponse<T>;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`Failed to parse JSON response: ${text}`);
  }

  if (!json.success) {
    throw apiErrorFrom(json.error.message, json.error.code, res.status);
  }

  return json.data;
}
